import { AsyncHelper } from './../../framework/async-helper';
import { IdentityService, Language } from './identity/identity.service';
import { TranslateService } from '@ngx-translate/core';
import { Logger, LoggerFactory } from './logger.service';
import { DestroyRef, EventEmitter, Inject, Injectable, Injector, LOCALE_ID, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AdminSettings } from 'app/shared/services/admin-settings.model';
import { SettingsService } from 'app/shared/services/admin-settings.service';
import { CurrentContextService } from './current-context.service';
import { UserLanguage } from 'app/account-settings/models/user.model';

export { Language } from './identity/identity.service';

interface LanguageFile {
  language: Language;
  fileName: string;
}

@Injectable()
export class LanguageService {
  private static supportedLanguages: Array<LanguageFile> = [
    { language: Language.English, fileName: 'en' },
    { language: Language.Swedish, fileName: 'sv' },
  ];
  // this language will be used as a fallback when a translation isn't found in the current language
  private static defaultLanguage = {
    language: Language.English,
    fileName: 'en',
  };
  private static readonly persistedLanguageKey = 'preferred-language';

  public languageChanged = new EventEmitter<Language>();

  private currentLanguage: Language;
  private isInitialized = false;
  private readonly logger: Logger;
  public adminLanguage: Language;
  public userLanguage: Language;

  // Lazy to keep test beds that don't wire CurrentContextService working.
  private _currentContextService: CurrentContextService | undefined;
  private readonly destroyRef = inject(DestroyRef);

  constructor(
    loggerFactory: LoggerFactory,
    private readonly translateService: TranslateService,
    @Inject(LOCALE_ID) public locale: string,
    private readonly identityService: IdentityService,
    private readonly injector: Injector,
    readonly settingsService: SettingsService,
  ) {
    this.logger = loggerFactory.get('LanguageService');

    Promise.resolve().then(() => this.bindToCurrentContext());

    settingsService.settings$.subscribe((settings: AdminSettings) => {
      this.adminLanguage = settings.adminLanguage;
      if (this.userLanguage) {
        this.switchLanguage(this.userLanguage);
      }
    });
    // Log language changes
    this.languageChanged.subscribe((lang) => this.logger.info(`Current language is ${Language[lang]}`));
  }

  private bindToCurrentContext(): void {
    try {
      this._currentContextService = this.injector.get(CurrentContextService);
    } catch {
      return;
    }
    this._currentContextService.currentContextBS.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((context) => {
      const language = mapUserLanguage(context?.user?.language);
      if (language !== undefined) {
        this.handleLanguage(language);
      } else if (!this.isInitialized) {
        this.initialize();
      }
    });
  }

  private handleLanguage(language: Language): void {
    this.initialize();
    this.userLanguage = language;
    if (language !== this.currentLanguage) {
      this.switchLanguage(language);
    }
  }

  public initialize() {
    this.isInitialized = false;
    this.checkSanity();
    this.logger.debug('Initializing');

    // Init internal translation service
    const fileNames = LanguageService.supportedLanguages.map((l) => l.fileName);
    this.translateService.addLangs(fileNames);

    // Set default language to the internal service
    const defaultLanguageFileName = LanguageService.defaultLanguage.fileName;
    this.translateService.setDefaultLang(defaultLanguageFileName);

    // Set current language
    this.setCurrentLanguage();

    this.logger.info(`Initialized.
                          Current language: ${Language[this.currentLanguage]}
                          Supported languages: ${JSON.stringify(LanguageService.supportedLanguages)}
                          Default language: ${JSON.stringify(LanguageService.defaultLanguage)}`);
    this.isInitialized = true;
  }

  public async getCurrentLanguageAsync(): Promise<Language> {
    await this.waitUntilInitializedAsync();
    return this.currentLanguage;
  }

  public async getCurrentLanguageNameAsync(): Promise<string> {
    // Get current language
    const currentLanguage = await this.getCurrentLanguageAsync();
    if (!currentLanguage) {
      this.logger.error('Current language is undefined');
      return undefined;
    }
    const langString = Language[currentLanguage].toLowerCase();
    return langString;
  }

  private setCurrentLanguage() {
    let language = this.findSuitableLanguage();
    if (language === Language.Unknown) {
      language = LanguageService.defaultLanguage.language;
    }
    this.userLanguage = language;
    this.switchLanguage(language);
  }

  public getLocaleFromCurrentLanguage(): string {
    if (this.currentLanguage === Language.Swedish) {
      return 'sv_SE';
    } else if (this.currentLanguage === Language.English) {
      return 'en_GB';
    } else {
      return this.locale;
    }
  }

  public async translateAsync(key: string, translateParams?: any): Promise<any> {
    await this.waitUntilInitializedAsync();
    if (!key) {
      this.logger.error(`Key is empty or null. Ensure the key "${key}" has a value.`);
    }
    const value = await this.translateService.get(key, translateParams).toPromise();
    if (!value) {
      this.logger.warn(`No translation found for ${key}`);
    }

    const valueCopy = JSON.parse(JSON.stringify(value));
    return valueCopy;
  }

  public translateInstantly(key: string, translateParams?: any): any {
    if (!key) {
      this.logger.error(`Key is empty or null. Ensure the key "${key}" has a value.`);
    }
    return this.translateService.instant(key, translateParams);
  }

  public switchLanguage(language: Language) {
    const requestedLanguage = language;
    if (this.adminLanguage) {
      language = this.adminLanguage;
    }
    const existingLanguage = LanguageService.supportedLanguages.find((l) => l.language === language);
    if (!existingLanguage) {
      this.logger.error(`${language} is not supported.
                               Supported languages are : ${JSON.stringify(LanguageService.supportedLanguages)}`);
    }
    try {
      this.translateService.use(existingLanguage.fileName);
    } catch (e) {
      this.logger.error(`Could not switch to ${Language[language]}.
                               File name : ${existingLanguage.fileName}.
                               Exception: ${JSON.stringify(e)}`);
    }
    this.currentLanguage = language;
    this.persistLanguage(requestedLanguage);
    this.languageChanged.emit(language);
  }

  private findSuitableLanguage(): Language {
    // Try find from identity
    const identityLanguage = this.getIdentityLanguage();
    if (identityLanguage !== Language.Unknown) {
      this.logger.debug(`The current language is set from the current identity to ${Language[identityLanguage]}`);
      return identityLanguage;
    }

    const persistedLanguage = this.getPersistedLanguage();
    if (persistedLanguage !== Language.Unknown) {
      this.logger.debug(`The current language is set from the persisted preference to ${Language[persistedLanguage]}`);
      return persistedLanguage;
    }

    // Try find from browser
    const browserLanguage = this.getBrowserLanguage();
    if (browserLanguage !== Language.Unknown) {
      this.logger.debug(`The current language is set from the browser language to ${Language[browserLanguage]}`);
      return browserLanguage;
    }

    // Could not find anything, return empty
    return Language.Unknown;
  }

  private getBrowserLanguage(): Language {
    if (window && window.navigator && window.navigator.language) {
      const lang = this.findLanguageByName(window.navigator.language);
      if (lang) {
        this.logger.debug('Language is found using window.navigator.language');
        return lang;
      }
    }
    if (navigator && navigator.language) {
      const lang = this.findLanguageByName(navigator.language);
      if (lang) {
        this.logger.debug('Language is found using navigator.language');
        return lang;
      }
    }
    if (navigator && (<any>navigator).userLanguage) {
      const lang = this.findLanguageByName((<any>navigator).userLanguage);
      if (lang) {
        this.logger.debug('Language is found using navigator.userLanguage');
        return lang;
      }
    }
    if (window && window.navigator && (<any>window).navigator.browserLanguage) {
      const lang = this.findLanguageByName((<any>window).navigator.browserLanguage);
      if (lang) {
        this.logger.debug('Language is found using navigator.browserLanguage');
        return lang;
      }
    }
    if (
      window &&
      window.navigator &&
      (<any>window).navigator.languages &&
      (<any>window).navigator.languages.length > 0
    ) {
      const lang = this.findLanguageByName((<any>window).navigator.languages[0]);
      if (lang) {
        this.logger.debug('Language is found using navigator.languages');
        return lang;
      }
    }
    return Language.Unknown;
  }

  private getIdentityLanguage(): Language {
    if (!this.identityService.hasCurrentIdentity) {
      return Language.Unknown;
    }
    const userLanguage = this._currentContextService?.currentContext?.user?.language;
    return mapUserLanguage(userLanguage) ?? Language.Unknown;
  }

  private getPersistedLanguage(): Language {
    try {
      const raw = localStorage.getItem(LanguageService.persistedLanguageKey);
      if (!raw) {
        return Language.Unknown;
      }
      const name = JSON.parse(raw) as keyof typeof Language;
      return Language[name] ?? Language.Unknown;
    } catch {
      return Language.Unknown;
    }
  }

  private persistLanguage(language: Language): void {
    try {
      localStorage.setItem(LanguageService.persistedLanguageKey, JSON.stringify(Language[language]));
    } catch {
      // best-effort; storage may be unavailable
    }
  }

  private checkSanity() {
    // Sanity check: At least one language must be supported.
    if (!LanguageService.supportedLanguages || LanguageService.supportedLanguages.length === 0) {
      this.logger.error('Application is not supported by any language?');
    }
    // Sanity check: There must be a supported language
    if (!LanguageService.defaultLanguage) {
      LanguageService.defaultLanguage = LanguageService.supportedLanguages[0];
      this.logger.warn(`There was no default language. Fallback has been set to ${LanguageService.defaultLanguage}`);
    }
  }

  private findLanguageByName(langString: string): Language | undefined {
    const lang = LanguageService.supportedLanguages.find(
      (l) => l.fileName === langString || l.fileName.includes(langString),
    );
    if (!lang) {
      if (langString.indexOf('-') !== -1) {
        langString = langString.split('-')[0];
        return this.findLanguageByName(langString);
      }
      if (langString.indexOf('_') !== -1) {
        langString = langString.split('_')[0];
        return this.findLanguageByName(langString);
      }
      return undefined;
    }
    return lang.language;
  }

  private waitUntilInitializedAsync(): Promise<void> {
    if (this.isInitialized) {
      return;
    }
    return AsyncHelper.waitUntilTrueAsync(
      () => this.isInitialized,
      () => Promise.resolve(),
      () => Promise.reject('Languages could not be initalized.'),
    );
  }
}

function mapUserLanguage(userLanguage: UserLanguage | undefined): Language | undefined {
  switch (userLanguage) {
    case UserLanguage.Swedish:
      return Language.Swedish;
    case UserLanguage.English:
      return Language.English;
    default:
      return undefined;
  }
}
